<template>
  <div
    class="hero-compare-showcase"
    :class="`hero-compare-showcase--${variant}`"
  >
    <h3
      v-if="variant === 'upload'"
      class="hero-compare-showcase__title task-card-title"
    >
      {{ t("landingB.uploadShowcase.title") }}
    </h3>
    <div class="hero-b-compare">
      <!-- 上：两张对比图 + gap 标注 -->
      <div class="hero-b-compare__stage">
        <div class="hero-b-compare__images">
          <figure class="hero-b-compare__frame hero-b-compare__frame--raw">
            <span class="hero-b-compare__corner hero-b-compare__corner--raw">
              <span class="hero-b-compare__corner-dot" aria-hidden="true"></span>
              <span class="hero-b-compare__corner-text">RAW</span>
            </span>
            <img
              :src="comparisonImages[0].src"
              :alt="t(comparisonImages[0].altKey)"
              class="hero-b-compare__image"
              loading="eager"
              decoding="async"
            />
          </figure>
          <figure class="hero-b-compare__frame hero-b-compare__frame--reference">
            <span class="hero-b-compare__corner hero-b-compare__corner--reference">
              <span class="hero-b-compare__corner-dot" aria-hidden="true"></span>
              <span class="hero-b-compare__corner-text">REFERENCE</span>
            </span>
            <img
              :src="comparisonImages[1].src"
              :alt="t(comparisonImages[1].altKey)"
              class="hero-b-compare__image"
              loading="eager"
              decoding="async"
            />
          </figure>

          <!-- 中央对比节点：呈现 raw / reference 两图的差异对照（非单向一步处理） -->
          <div class="hero-b-compare__seam" aria-hidden="true">
            <span class="hero-b-compare__seam-line"></span>
            <span class="hero-b-compare__seam-node">
              <!-- 对照标记：VS，表达 raw 与 reference 两图的互相参照/差异对照 -->
              <span class="hero-b-compare__seam-label">VS</span>
            </span>
          </div>
        </div>

        <!-- gap 标注标签：标题常显，hover 展开详情浮层 -->
        <div
          v-for="gap in gapAnnotations"
          :key="gap.id"
          class="hero-b-gap"
          :style="gapPosition(gap)"
        >
          <button
            type="button"
            class="hero-b-gap__tag"
            :aria-label="gap.title"
            :aria-expanded="revealedGapId === gap.id"
            @mouseenter="handleGapReveal(gap)"
            @focus="handleGapReveal(gap)"
            @click="toggleGapReveal(gap)"
          >
            {{ gap.title }}
          </button>
          <div
            class="hero-b-gap__popover"
            role="tooltip"
            :class="{ 'is-open': revealedGapId === gap.id }"
          >
            <div class="hero-b-gap__popover-head">
              <span class="hero-b-gap__title">{{ gap.title }}</span>
              <span class="hero-b-gap__severity">{{ gap.severityLabel }}</span>
            </div>
            <p class="hero-b-gap__brief">{{ gap.brief }}</p>
            <p v-if="gap.severityReason" class="hero-b-gap__reason">
              {{ gap.severityReason }}
            </p>
          </div>
        </div>
      </div>

      <!-- 下：AI 后期分析预览 + 完整案例 CTA（上传页变体横向排布于对比图右侧） -->
      <div class="hero-b-compare__aside">
        <div
          v-if="aiDetailPreviewHtml"
          class="hero-b-ai"
          :class="{ 'is-open': aiRevealed }"
          @mouseenter="handleAiPreviewReveal"
          @focusin="handleAiPreviewReveal"
        >
          <div
            class="hero-b-ai-preview"
            role="button"
            tabindex="0"
            :aria-label="t('resultView.taskSummary.aiDetailCta')"
            :aria-expanded="aiRevealed"
            @click="toggleAiReveal"
            @keydown.enter.prevent="toggleAiReveal"
            @keydown.space.prevent="toggleAiReveal"
          >
            <div class="hero-b-ai-preview__body">
              <div class="hero-b-ai-preview__head">
                <span class="hero-b-ai-preview__label">{{
                  t("resultView.taskSummary.aiDetailTitle")
                }}</span>
              </div>
              <div
                class="hero-b-ai-preview__content"
                v-html="aiDetailPreviewHtml"
              />
            </div>
            <div
              class="hero-b-ai-preview__overlay"
              aria-hidden="true"
            />
          </div>

          <!-- hover 浮层：展示原弹窗完整内容，样式与 gap tag 浮层一致，尺寸接近弹窗 -->
          <div class="hero-b-ai-popover" role="tooltip">
            <div class="hero-b-ai-popover__head">
              <span class="hero-b-ai-popover__title">{{
                t("resultView.taskSummary.aiDetailTitle")
              }}</span>
            </div>
            <div
              class="hero-b-ai-popover__body markdown-body"
              v-html="aiGuideHtml"
            ></div>
          </div>
        </div>

        <!-- 完整案例 CTA（方案 10.1）：进入公开只读案例页 -->
        <!-- 使用原生 <a target="_blank"> 打开新标签：浏览器不会将其视为弹窗拦截，
             且 rel="noopener noreferrer" 等价于原 window.open 的后两个 features 参数；
             同时避免 window.open 在 noopener 下返回 null 导致误报 popup_blocked。
             新页面会自行上报 content_page_viewed，与原页面 navigation_completed success 形成可关联的打开证据。 -->
        <a
          type="button"
          class="hero-b-case-cta"
          data-test="hero-b-public-result-case"
          :href="publicCaseHref"
          target="_blank"
          rel="noopener noreferrer"
          @click="handlePublicCaseClick"
        >
          <span class="hero-b-case-cta__label">{{
            t("landingB.caseStudy.viewFullCase")
          }}</span>
          <i class="pi pi-arrow-right" aria-hidden="true" />
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { markdownRenderer } from "@/core/markdown/markdownRenderer";
import {
  heroBComparisonImages as comparisonImages,
  heroBGapAnnotations,
  heroBAiGuideMdKey,
} from "@/core/data/heroFeaturesBContent";

const props = defineProps({
  // 'landing'：沿用 HeroFeaturesB 深色 hero 外观；'upload'：浅色卡片外壳 + 单行紧凑布局
  variant: {
    type: String,
    default: "landing",
    validator: (v) => ["landing", "upload"].includes(v),
  },
});

const emit = defineEmits(["content-revealed", "case-cta-clicked"]);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// gap 标注：结构（id / severity 枚举 / pos / i18n key）来自数据文件
// heroFeaturesBContent，文案通过 i18n key 解析。severity 复用 resultView 的
// 既有 severity 文案（major / moderate…）。数据源自 result subject 2d9be7a0
// 的 AI job 缓存 analysisPayload.gaps[]。
const gapAnnotations = computed(() =>
  heroBGapAnnotations.map((gap) => ({
    id: gap.id,
    pos: gap.pos,
    title: t(gap.titleKey),
    brief: t(gap.briefKey),
    severityReason: t(gap.reasonKey),
    severityLabel: t(`resultView.aiAnalysis.severity.${gap.severity}`),
  }))
);

const gapPosition = (gap) => ({ ...gap.pos });

// AI 后期分析（编辑建议）：markdown 文案来自 i18n（key 见数据文件 heroBAiGuideMdKey），
// 数据源自同一 AI job 缓存 analysisPayload.overview.postProcessingSuggestionsMd。
// 预览框与弹窗均复用 TaskSummaryCard 模式：预览取 markdown 前 2 行，浮层渲染完整内容。
const aiPostProcessingGuideMd = computed(() => t(heroBAiGuideMdKey));

const aiGuideHtml = computed(() =>
  markdownRenderer.render(aiPostProcessingGuideMd.value)
);

const aiDetailPreviewHtml = computed(() => {
  const lines = aiPostProcessingGuideMd.value
    .split("\n")
    .filter((line) => line.trim());
  if (lines.length === 0) return "";
  return markdownRenderer.render(lines.slice(0, 2).join("\n"));
});

// B 独有交互（gap 差距标注 / AI 分析预览）为 hover 展开浮层，无点击动作。
// 采用「首次 hover 去重」策略：每个稳定 content_id 在本次页面生命周期内仅上报一次，
// 避免反复悬停产生高频噪声（对齐方案 §3 原则 6「曝光去重，互动按真实动作上报」）。
// content_id 使用稳定 code（gap_1..gap_4 / ai_post_processing），不上报 gap 标题、
// severity 文案或 AI 建议原文（对齐方案 §5.5 禁止字段）。
const revealedContentKeys = new Set();

// 复用 content_revealed 语义承接内容互动，不新增事件名；由父级接入对应域的 analytics。
// method 使用 hover 表达触发方式；element_type=button 因 gap tag 与 AI 预览均为
// 可聚焦的按钮/角色按钮元素。
const trackContentReveal = ({ elementName, contentId, contentType }) => {
  if (revealedContentKeys.has(contentId)) {
    return;
  }
  revealedContentKeys.add(contentId);
  emit("content-revealed", { elementName, contentId, contentType });
};

const handleGapReveal = (gap) => {
  trackContentReveal({
    elementName: "hero_b_gap_tag",
    contentId: gap.id,
    contentType: "hero_gap",
  });
};

const handleAiPreviewReveal = () => {
  trackContentReveal({
    elementName: "hero_b_ai_preview",
    contentId: "ai_post_processing",
    contentType: "ai_analysis",
  });
};

// 阶段0：触屏 tap 兜底。桌面端浮层由 :hover 触发（保留）；触屏无 hover，
// 改为受控「点标签展开 / 再点收起 / 点空白收起」，aria-expanded 同步。
const revealedGapId = ref(null);
const aiRevealed = ref(false);

const toggleGapReveal = (gap) => {
  if (revealedGapId.value === gap.id) {
    revealedGapId.value = null;
    return;
  }
  // 首次展开上报曝光埋点（去重由 trackContentReveal 内部 Set 负责）
  handleGapReveal(gap);
  revealedGapId.value = gap.id;
};

const toggleAiReveal = () => {
  if (!aiRevealed.value) {
    handleAiPreviewReveal();
  }
  aiRevealed.value = !aiRevealed.value;
};

// 点击浮层/标签区域之外时收起受控态；捕获阶段先于按钮 click，
// 但 closest 命中 .hero-b-gap / .hero-b-ai 即跳过，不影响按钮自身的 toggle。
const closeAllReveals = (event) => {
  const target = event.target;
  if (!target || typeof target.closest !== "function") return;
  if (target.closest(".hero-b-gap") || target.closest(".hero-b-ai")) return;
  revealedGapId.value = null;
  aiRevealed.value = false;
};

onMounted(() => {
  if (typeof document !== "undefined") {
    document.addEventListener("click", closeAllReveals, true);
  }
});

onBeforeUnmount(() => {
  if (typeof document !== "undefined") {
    document.removeEventListener("click", closeAllReveals, true);
  }
});

// 公开案例 slug（方案 6.3 allowlist 中的稳定 slug，非真实 Subject UUID）。
const PUBLIC_CASE_SLUG = "architecture";

// 完整案例 href：用 router.resolve 动态解析，避免硬编码 URL / 真实 UUID。
// 绑定到原生 <a target="_blank" rel="noopener noreferrer">，由浏览器处理新标签打开，
// 不会被弹出拦截器拦截，也无需 window.open 的 popup_blocked 分支（window.open 在
// noopener 下返回 null 会误报 popup_blocked）。新页面会自行上报 content_page_viewed。
const publicCaseHref = computed(() =>
  router.resolve({
    name: "public-result-case",
    params: {
      lang: route.params.lang || "en",
      caseSlug: PUBLIC_CASE_SLUG,
    },
  }).href
);

// 案例 CTA 点击：原生链接由浏览器跳转（新标签打开可靠，不触发弹窗拦截），
// 直接上报 success。稳定上下文字段随 emit 交由父级接入对应域 analytics。
const handlePublicCaseClick = () => {
  emit("case-cta-clicked", {
    source_position: props.variant === "upload" ? "upload_showcase" : "landing_hero_b",
    element_name: "hero_b_public_result_case",
    element_type: "button",
    cta_type: "case_study",
    destination_type: "route",
    destination_id: "public_result_case",
    content_id: "architecture_color_match",
    content_type: "result_case",
  });
};
</script>

<style scoped>
@import "../result/styles/taskCardHeader.shared.css";

/* 组件根建立独立 stacking context（根因修复）：
   内部 gap 标签 20 / hover 40 / 浮层 50 / 接缝 25 / 角标 15 这些层级是「组件内部排序用」，
   不是页面层级。若不隔离，它们会泄漏到页面级与 upload 页的恢复/压缩蒙层
   （.upload-restore-overlay / .upload-compressing-overlay）直接竞争 ——
   同值时后绘制的本组件胜出，浮层(50)更是稳赢，于是蒙层上方浮出标签与「编辑建议」浮层。
   isolation 只新建上下文、不裁剪，浮层仍可溢出卡片外。 */
.hero-compare-showcase {
  isolation: isolate;
}

.hero-b-compare {
  position: relative;
  width: 100%;
  max-width: 72rem;
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.hero-b-compare__stage {
  position: relative;
  padding: 0.5rem;
  border-radius: 1.3rem;
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--surface-overlay) 16%, transparent) 0%,
    color-mix(in srgb, var(--theme-accent) 12%, transparent) 100%
  );
  border: 1px solid color-mix(in srgb, var(--zinc-50) 10%, transparent);
  box-shadow: 0 28px 60px color-mix(in srgb, var(--shadow-color) 78%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 12%, transparent);
}

.hero-b-compare__images {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}

/* 每张对比图的外框：承载角标与品牌化描边，强化 raw / reference 的视觉区分 */
.hero-b-compare__frame {
  position: relative;
  margin: 0;
  border-radius: 1.1rem;
}

.hero-b-compare__frame--raw {
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--zinc-50) 12%, transparent);
}

.hero-b-compare__frame--reference {
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--theme-accent) 30%, transparent),
    0 0 26px color-mix(in srgb, var(--theme-accent) 14%, transparent);
}

.hero-b-compare__image {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 1rem;
  background: color-mix(in srgb, var(--zinc-950) 90%, var(--theme-primary-hover) 10%);
}

/* upload 图（左侧 raw）取上方区域，下方超出红框部分不显示 */
.hero-b-compare__frame--raw .hero-b-compare__image {
  object-position: top;
}

/* 角标：RAW（左上）/ REFERENCE（右上） */
.hero-b-compare__corner {
  position: absolute;
  z-index: 15;
  display: inline-flex;
  align-items: center;
  gap: 0.34rem;
  padding: 0.24rem 0.5rem;
  border-radius: 0.38rem;
  font-family: "SF Mono", ui-monospace, "Inter", "SF Pro Text", -apple-system,
    BlinkMacSystemFont, sans-serif;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--zinc-300) 88%, var(--zinc-50));
  background: color-mix(in srgb, var(--zinc-950) 76%, #000);
  border: 1px solid color-mix(in srgb, var(--zinc-50) 10%, transparent);
  box-shadow: 0 3px 9px color-mix(in srgb, #000 30%, transparent);
  pointer-events: none;
}

.hero-b-compare__corner--raw {
  top: 0.6rem;
  left: 0.6rem;
}

.hero-b-compare__corner--reference {
  top: 0.6rem;
  right: 0.6rem;
}

/* 角标为纯文字铭牌，去掉圆点以区别于 gap 标记 */
.hero-b-compare__corner-dot {
  display: none;
}

/* 中央对比节点：raw / reference 两图的差异对照指示器（非动作按钮） */
.hero-b-compare__seam {
  position: absolute;
  top: 0.5rem;
  bottom: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 25;
}

.hero-b-compare__seam-line {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    color-mix(in srgb, var(--zinc-50) 26%, transparent) 16%,
    color-mix(in srgb, var(--zinc-50) 26%, transparent) 84%,
    transparent 100%
  );
}

/* 玻璃指示徽标：中性、非 CTA，强调"对照分析"而非"一步处理" */
.hero-b-compare__seam-node {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.7rem;
  height: 2.7rem;
  border-radius: 999px;
  color: var(--text-on-dark);
  background: color-mix(in srgb, var(--zinc-900) 82%, transparent);
  border: 1px solid color-mix(in srgb, var(--theme-accent) 42%, var(--zinc-50) 12%);
  box-shadow: 0 8px 20px color-mix(in srgb, #000 45%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 14%, transparent),
    0 0 0 4px color-mix(in srgb, var(--surface-card) 70%, transparent);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.3s ease;
}

.hero-b-compare__seam-label {
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
  color: color-mix(in srgb, var(--zinc-50) 88%, var(--theme-accent));
  text-shadow: 0 1px 2px color-mix(in srgb, #000 55%, transparent);
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-compare__stage:hover .hero-b-compare__seam-node {
    transform: scale(1.07);
    border-color: color-mix(in srgb, var(--theme-accent) 64%, var(--zinc-50) 14%);
  }
}

/* gap 标注标签 */
.hero-b-gap {
  position: absolute;
  z-index: 20;
}

/* 展开态（键盘 focus / 触屏点击）提升当前 gap 容器层级，使其浮层覆盖其余 tag；
   桌面 hover 也保留（见下方 hover 媒体查询）。 */
.hero-b-gap:focus-within {
  z-index: 40;
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-gap:hover {
    z-index: 40;
  }
}

.hero-b-gap__tag {
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  padding: 0.32rem 0.74rem 0.32rem 0.6rem;
  border-radius: 999px;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: color-mix(in srgb, var(--zinc-50) 92%, var(--theme-accent));
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--zinc-900) 74%, transparent),
    color-mix(in srgb, var(--zinc-950) 82%, transparent)
  );
  border: 1px solid color-mix(in srgb, var(--theme-accent) 26%, color-mix(in srgb, var(--zinc-50) 14%, transparent));
  box-shadow: 0 8px 20px color-mix(in srgb, #000 42%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 14%, transparent),
    0 0 0 1px color-mix(in srgb, var(--theme-accent) 12%, transparent),
    0 0 16px color-mix(in srgb, var(--theme-accent) 16%, transparent);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.35s ease, box-shadow 0.35s ease, color 0.35s ease;
}

.hero-b-gap__tag::before {
  content: "";
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 999px;
  flex-shrink: 0;
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-accent));
  box-shadow: 0 0 8px color-mix(in srgb, var(--theme-accent) 60%, transparent);
  animation: hero-b-gap-pulse 2.4s ease-in-out infinite;
}

@keyframes hero-b-gap-pulse {
  0%,
  100% {
    box-shadow: 0 0 6px color-mix(in srgb, var(--theme-accent) 42%, transparent);
  }
  50% {
    box-shadow: 0 0 13px color-mix(in srgb, var(--theme-accent) 78%, transparent);
  }
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-gap__tag:hover {
    transform: translateY(-1px) scale(1.04);
    color: color-mix(in srgb, var(--zinc-50) 100%, transparent);
    border-color: color-mix(in srgb, var(--theme-accent) 52%, transparent);
    box-shadow: 0 12px 26px color-mix(in srgb, #000 50%, transparent),
      0 0 0 3px color-mix(in srgb, var(--theme-accent) 12%, transparent),
      inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 18%, transparent);
  }
}

/* 触屏按压反馈 */
.hero-b-gap__tag:active {
  transform: scale(0.96);
  border-color: color-mix(in srgb, var(--theme-accent) 52%, transparent);
}

.hero-b-gap__tag:focus-visible {
  outline: none;
  border-color: color-mix(in srgb, var(--theme-accent) 60%, transparent);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--theme-accent) 55%, transparent);
}

.hero-b-gap__popover {
  position: absolute;
  bottom: calc(100% + 0.6rem);
  left: 0;
  z-index: 50;
  width: max(14rem, 16vw);
  padding: 0.9rem 1rem;
  border-radius: 0.95rem;
  background: linear-gradient(
    162deg,
    color-mix(in srgb, var(--zinc-900) 88%, transparent),
    color-mix(in srgb, var(--zinc-950) 94%, transparent)
  );
  border: 1px solid color-mix(in srgb, var(--zinc-50) 12%, transparent);
  box-shadow: 0 22px 48px color-mix(in srgb, #000 55%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 12%, transparent);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  opacity: 0;
  visibility: hidden;
  transform: translateY(0.4rem);
  transition: opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), visibility 0.28s ease;
  pointer-events: none;
}

/* 顶部品牌化 accent 细线 */
.hero-b-gap__popover::before {
  content: "";
  position: absolute;
  top: 0;
  left: 1rem;
  right: 1rem;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--theme-accent) 70%, transparent),
    transparent
  );
}

/* 上方标签（top 定位）的浮层向下展开，避免被 header 遮挡 */
.hero-b-gap[style*="top"] .hero-b-gap__popover {
  top: calc(100% + 0.5rem);
  bottom: auto;
  transform: translateY(-0.35rem);
}

/* 右侧标签的浮层避免溢出视口：向右对齐 */
.hero-b-gap[style*="right"] .hero-b-gap__popover {
  left: auto;
  right: 0;
}

/* 触屏 tap 兜底：受控展开态（is-open）与桌面 hover 同等展示浮层 */
.hero-b-gap__popover.is-open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  pointer-events: auto;
}

.hero-b-gap[style*="top"] .hero-b-gap__popover.is-open {
  transform: translateY(0);
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-gap__tag:hover + .hero-b-gap__popover,
  .hero-b-gap__popover:hover {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
  }
}

.hero-b-gap__popover-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.hero-b-gap__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: color-mix(in srgb, var(--zinc-50) 96%, var(--theme-accent));
}

.hero-b-gap__severity {
  flex-shrink: 0;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: color-mix(in srgb, var(--zinc-50) 86%, var(--theme-accent));
  background: color-mix(in srgb, var(--theme-accent) 18%, transparent);
  border: 1px solid color-mix(in srgb, var(--theme-accent) 40%, transparent);
}

.hero-b-gap__brief {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.55;
  color: color-mix(in srgb, var(--zinc-100) 80%, var(--zinc-400));
}

.hero-b-gap__reason {
  margin: 0.4rem 0 0;
  padding-top: 0.4rem;
  border-top: 1px solid color-mix(in srgb, var(--zinc-50) 8%, transparent);
  font-size: 0.74rem;
  line-height: 1.5;
  color: color-mix(in srgb, var(--zinc-300) 70%, var(--zinc-500, var(--zinc-400)));
}

/* AI 后期分析预览内嵌框：暗色深底玻璃面板 */
.hero-b-ai-preview {
  position: relative;
  display: block;
  border: 1px solid color-mix(in srgb, var(--zinc-50) 16%, transparent);
  border-radius: 1rem;
  overflow: hidden;
  max-height: 118px;
  cursor: pointer;
  padding: 0.85rem 1rem;
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--zinc-900) 92%, transparent),
    color-mix(in srgb, var(--zinc-950) 96%, transparent)
  );
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  box-shadow:
    0 8px 20px color-mix(in srgb, #000 40%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 10%, transparent),
    inset 0 -1px 0 color-mix(in srgb, #000 25%, transparent);
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.3s ease, border-color 0.3s ease;
}

/* 顶部品牌化 accent 细线：在深色底板上清晰可辨的引导标识 */
.hero-b-ai-preview::before {
  content: "";
  position: absolute;
  top: 0;
  left: 1rem;
  right: 1rem;
  height: 2px;
  z-index: 2;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--theme-accent) 75%, transparent),
    transparent
  );
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-ai-preview:hover {
    transform: translateY(-2px);
    border-color: color-mix(in srgb, var(--zinc-50) 26%, transparent);
    box-shadow:
      0 14px 30px color-mix(in srgb, #000 48%, transparent),
      inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 16%, transparent),
      inset 0 -1px 0 color-mix(in srgb, #000 28%, transparent);
  }
}

/* 触屏按压反馈 */
.hero-b-ai-preview:active {
  transform: scale(0.98);
}

.hero-b-ai-preview:focus-visible {
  outline: none;
  box-shadow:
    0 14px 30px color-mix(in srgb, #000 48%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 16%, transparent),
    0 0 0 3px color-mix(in srgb, var(--theme-focus-ring) 55%, transparent);
}

.hero-b-ai-preview__body {
  position: relative;
  z-index: 1;
}

.hero-b-ai-preview__head {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 6px;
}

.hero-b-ai-preview__label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--zinc-50) 96%, var(--theme-accent)),
    color-mix(in srgb, var(--zinc-50) 80%, var(--theme-accent))
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-b-ai-preview__content {
  position: relative;
  z-index: 1;
  color: color-mix(in srgb, var(--zinc-100) 88%, var(--zinc-300));
  font-size: 0.82rem;
  line-height: 1.55;
}

.hero-b-ai-preview__content :deep(p) {
  margin: 0 0 0.4rem;
}

.hero-b-ai-preview__content :deep(p:last-child) {
  margin-bottom: 0;
}

.hero-b-ai-preview__overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 56px;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    color-mix(in srgb, var(--zinc-950) 72%, transparent) 35%,
    color-mix(in srgb, var(--zinc-950) 97%, transparent) 100%
  );
}

/* AI 分析 hover 浮层：交互与样式对齐上方 gap tag 浮层，尺寸接近原弹窗 */
.hero-b-ai {
  position: relative;
  z-index: 20;
}

/* 展开态（focus / 触屏点击）提升容器层级；桌面 hover 也保留 */
.hero-b-ai:focus-within {
  z-index: 40;
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-ai:hover {
    z-index: 40;
  }
}

.hero-b-ai-popover {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%) translateY(0.4rem);
  z-index: 50;
  width: min(40rem, calc(100vw - 2rem));
  max-height: min(52vh, 420px);
  display: flex;
  flex-direction: column;
  padding: 1rem 1.1rem;
  border-radius: 0.95rem;
  background: linear-gradient(
    162deg,
    color-mix(in srgb, var(--zinc-900) 88%, transparent),
    color-mix(in srgb, var(--zinc-950) 94%, transparent)
  );
  border: 1px solid color-mix(in srgb, var(--zinc-50) 12%, transparent);
  box-shadow: 0 22px 48px color-mix(in srgb, #000 55%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 12%, transparent);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), visibility 0.28s ease;
  pointer-events: none;
}

/* 顶部品牌化 accent 细线（与 gap 浮层呼应） */
.hero-b-ai-popover::before {
  content: "";
  position: absolute;
  top: 0;
  left: 1rem;
  right: 1rem;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in srgb, var(--theme-accent) 70%, transparent),
    transparent
  );
}

/* 键盘 focus 展开（保留） */
.hero-b-ai:focus-within .hero-b-ai-popover {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
  pointer-events: auto;
}

/* 触屏 tap 兜底：受控展开态（is-open）与桌面 hover 同等展示浮层 */
.hero-b-ai.is-open .hero-b-ai-popover {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
  pointer-events: auto;
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-ai:hover .hero-b-ai-popover,
  .hero-b-ai-popover:hover {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
    pointer-events: auto;
  }
}

.hero-b-ai-popover__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid color-mix(in srgb, var(--zinc-50) 10%, transparent);
  flex-shrink: 0;
}

.hero-b-ai-popover__title {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  background-image: linear-gradient(
    120deg,
    color-mix(in srgb, var(--zinc-50) 96%, transparent),
    color-mix(in srgb, var(--zinc-50) 74%, var(--theme-accent))
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-b-ai-popover__body {
  overflow-y: auto;
  font-size: 0.88rem;
  line-height: 1.65;
  color: color-mix(in srgb, var(--zinc-100) 80%, var(--zinc-400));
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--zinc-50) 20%, transparent) transparent;
}

.hero-b-ai-popover__body :deep(p) {
  margin: 0 0 0.8rem;
}

.hero-b-ai-popover__body :deep(ol),
.hero-b-ai-popover__body :deep(ul) {
  margin: 0 0 0.8rem;
  padding-left: 1.25rem;
}

.hero-b-ai-popover__body :deep(li) {
  margin-bottom: 0.55rem;
}

.hero-b-ai-popover__body :deep(strong) {
  color: color-mix(in srgb, var(--zinc-50) 94%, var(--theme-accent));
  font-weight: 700;
}

.hero-b-ai-popover__body :deep(a) {
  color: color-mix(in srgb, var(--zinc-50) 70%, var(--theme-accent));
  text-decoration: underline;
  text-underline-offset: 2px;
}

.hero-b-ai-popover__body :deep(*:last-child) {
  margin-bottom: 0;
}

.hero-b-ai-popover__body::-webkit-scrollbar {
  width: 8px;
}

.hero-b-ai-popover__body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: color-mix(in srgb, var(--zinc-50) 16%, transparent);
}

.hero-b-ai-popover__body::-webkit-scrollbar-thumb:hover {
  background: color-mix(in srgb, var(--theme-accent) 40%, transparent);
}

/* 编辑预览框下方完整案例 CTA（方案 10.1）：次级按钮，弱于主 CTA 但保留品牌活力，
   深色 hero 背景上清晰可辨——accent 描边 + accent 微光晕 + 渐变文字 */
.hero-b-case-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.3rem;
  padding: 0.74rem 1.2rem;
  border-radius: 0.95rem;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: color-mix(in srgb, var(--zinc-50) 92%, var(--theme-accent));
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--zinc-900) 88%, transparent),
    color-mix(in srgb, var(--zinc-950) 92%, transparent)
  );
  border: 1px solid color-mix(in srgb, var(--theme-accent) 42%, var(--border-strong));
  box-shadow:
    0 10px 26px color-mix(in srgb, var(--shadow-color) 46%, transparent),
    0 0 0 1px color-mix(in srgb, var(--theme-accent) 14%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 12%, transparent);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.3s ease, box-shadow 0.3s ease, color 0.3s ease, gap 0.3s ease,
    background-color 0.3s ease;
}

.hero-b-case-cta__label {
  background-image: linear-gradient(
    to right,
    var(--theme-gradient-start),
    var(--theme-gradient-end)
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-b-case-cta .pi {
  font-size: 0.78rem;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

@media (hover: hover) and (pointer: fine) {
  .hero-b-case-cta:hover {
    transform: translateY(-2px);
    gap: 0.65rem;
    color: color-mix(in srgb, var(--zinc-50) 100%, var(--theme-accent));
    border-color: color-mix(in srgb, var(--theme-accent) 62%, transparent);
    background: linear-gradient(
      160deg,
      color-mix(in srgb, var(--zinc-900) 92%, transparent),
      color-mix(in srgb, var(--zinc-950) 95%, transparent)
    );
  }
}

/* 触屏按压反馈 */
.hero-b-case-cta:active {
  transform: scale(0.98);
  box-shadow:
    0 16px 34px color-mix(in srgb, var(--shadow-color) 56%, transparent),
    0 0 0 3px color-mix(in srgb, var(--theme-accent) 18%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 16%, transparent);
}

.hero-b-case-cta:hover .pi {
  transform: translateX(3px);
}

.hero-b-case-cta:focus-visible {
  outline: none;
  border-color: color-mix(in srgb, var(--theme-accent) 58%, transparent);
  box-shadow:
    0 0 0 3px color-mix(in srgb, var(--theme-accent) 48%, transparent),
    0 10px 26px color-mix(in srgb, var(--shadow-color) 46%, transparent);
}

/* 响应式：中屏 / 移动端对比区收紧 */
@media (max-width: 1023px) {
  .hero-b-compare {
    max-width: 34rem;
    gap: 0.7rem;
    /* 中屏底部留白：次级 CTA 不贴边 */
    padding-bottom: 1.25rem;
  }
}

@media (max-width: 767px) {
  .hero-b-compare {
    width: 100%;
    /* 移动端底部留白：避免次级 CTA（View full case study）紧贴组件下边缘 */
    padding-bottom: 1.5rem;
  }
}

/* 尊重系统减少动效偏好 */
@media (prefers-reduced-motion: reduce) {
  .hero-b-gap__tag,
  .hero-b-gap__popover,
  .hero-b-ai-preview,
  .hero-b-ai-popover,
  .hero-b-compare__seam-node,
  .hero-b-compare__image {
    transition: none;
  }

  .hero-b-case-cta {
    transition: none;
  }

  .hero-b-ai-preview:hover,
  .hero-b-gap__tag:hover,
  .hero-b-compare__stage:hover .hero-b-compare__seam-node,
  .hero-b-case-cta:hover {
    transform: none;
  }

  .hero-b-gap__tag::before {
    animation: none;
  }
}

/* ===== Upload 变体：固定深色卡片外壳（对齐 HeroFeaturesB 对比区）+ 单行紧凑 band，贴合上传页下方区域、守住一屏 ===== */
.hero-compare-showcase--upload {
  width: 100%;
  margin-top: 0.6rem;
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--zinc-50) 12%, transparent);
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--zinc-900) 90%, transparent) 0%,
    color-mix(in srgb, var(--zinc-950) 96%, transparent) 100%
  );
  box-shadow: 0 28px 60px color-mix(in srgb, var(--shadow-color) 46%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 12%, transparent);
}

/* Upload 变体独有标题：复用 tasklayout 子卡片统一 .task-card-title（左侧渐变竖条 +
   品牌色柔光晕 + 实色文字）。upload 卡片为固定深色底，覆盖 color 为浅色保证可读，
   并补 margin-bottom 与下方 compare 区域分隔（.task-card-title 自身 margin:0）。 */
.hero-compare-showcase--upload .hero-compare-showcase__title {
  margin-bottom: 0.75rem;
  color: color-mix(in srgb, var(--zinc-50) 94%, var(--theme-accent));
}

.hero-compare-showcase--upload .hero-b-compare {
  flex-direction: row;
  align-items: stretch;
  gap: 0.85rem;
  max-width: 100%;
}

.hero-compare-showcase--upload .hero-b-compare__stage {
  flex: 1.4 1 0;
  min-width: 0;
  padding: 0.35rem;
}

.hero-compare-showcase--upload .hero-b-compare__aside {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.65rem;
}

/* Upload 变体对比图：改用 object-fit: contain，完整展示竖图不裁切；
   同时抬高 max-height 让图片更舒展（仍受上传页一屏约束，下方 AI 预览/CTA 跟随居中）。 */
.hero-compare-showcase--upload .hero-b-compare__image {
  aspect-ratio: auto;
  max-height: 350px;
  object-fit: contain;
  background: color-mix(in srgb, var(--zinc-950) 90%, var(--theme-primary-hover) 10%);
}

/* Upload 变体：默认显示的 AI 编辑建议缩略框调高（仅改 upload，不影响 Landing 默认浮层），
   露出更多预览内容；底部渐隐遮罩（.hero-b-ai-preview__overlay 固定 56px）仍保留淡出效果。 */
.hero-compare-showcase--upload .hero-b-ai-preview {
  max-height: 200px;
}

.hero-compare-showcase--upload .hero-b-case-cta {
  width: 100%;
  margin-top: 0;
}

@media (max-width: 767px) {
  .hero-compare-showcase--upload .hero-b-compare {
    flex-direction: column;
  }
}

/* 触屏触控目标：gap 标注标签提到 ≥44px（P2-12），保证可点性 */
@media (hover: none) {
  .hero-b-gap__tag {
    min-height: 44px;
    align-items: center;
  }
}
</style>
